<template>
  <div class="page-wrapper">
    <div class="content container-fluid">
      <!-- Page header -->
      <div class="page-header">
        <div class="row align-items-center">
          <div class="col-md-6">
            <h3 class="page-title mb-1">Quản lý chấm công</h3>
            <ul class="breadcrumb mb-0">
              <li class="breadcrumb-item"><a href="/">Dashboard</a></li>
              <li class="breadcrumb-item active">Attendance</li>
            </ul>
          </div>
          <div class="col-md-6 d-flex justify-content-md-end mt-3 mt-md-0">
            <div class="btn-group mr-2" role="group" aria-label="View mode">
              <button
                class="btn btn-sm btn-outline-primary"
                :class="{ active: viewMode === 'icon' }"
                @click="viewMode = 'icon'"
              >
                <i class="fa fa-th mr-1"></i> Icon
              </button>
              <button
                class="btn btn-sm btn-outline-primary"
                :class="{ active: viewMode === 'cong' }"
                @click="viewMode = 'cong'"
              >
                <i class="fa fa-list-ol mr-1"></i> Số công
              </button>
            </div>

            <div class="d-flex align-items-center month-nav">
              <button
                class="btn btn-sm btn-outline-secondary"
                @click="prevMonth"
                :disabled="loading"
              >
                ‹
              </button>
              <div class="mx-2 font-weight-600">
                {{ ymLabel }}
              </div>
              <button
                class="btn btn-sm btn-outline-secondary"
                @click="nextMonth"
                :disabled="loading"
              >
                ›
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Filter + actions -->
      <div class="card mb-3">
        <div class="card-body pb-2">
          <div class="row">
            <div class="col-md-3 mb-2">
              <label class="form-label-sm">Tên/Đăng nhập</label>
              <input
                type="text"
                class="form-control form-control-sm"
                v-model.trim="filter.keyword"
                placeholder="Nhập tên hoặc username"
              >
            </div>
            <div class="col-md-3 mb-2">
              <label class="form-label-sm">Tháng</label>
              <input
                type="month"
                class="form-control form-control-sm"
                v-model="filter.month"
              >
            </div>
            <div class="col-md-3 mb-2">
              <label class="form-label-sm">Phòng ban</label>
              <select
                class="form-control form-control-sm"
                v-model="filter.phongBanId"
              >
                <option :value="null">Tất cả phòng ban</option>
                <option
                  v-for="pb in phongBans"
                  :key="pb.idPhongBan"
                  :value="pb.idPhongBan"
                >
                  {{ pb.tenPhongBan }}
                </option>
              </select>
            </div>
            <div class="col-md-3 mb-2 d-flex align-items-end">
              <button
                class="btn btn-sm btn-primary mr-2"
                @click="fetchMatrix"
                :disabled="loading"
              >
                <i class="fa fa-search mr-1"></i> Tìm
              </button>
              <button
                class="btn btn-sm btn-outline-success"
                @click="exportExcel"
                :disabled="loading || !matrix"
              >
                <i class="fa fa-file-excel-o mr-1"></i> Xuất Excel
              </button>
            </div>
          </div>

          <hr class="my-3">

          <div class="row align-items-end">
            <div class="col-md-3 mb-2">
              <label class="form-label-sm">Ngày áp dụng đóng ca</label>
              <input
                type="date"
                class="form-control form-control-sm"
                v-model="closeForm.date"
              >
            </div>
            <div class="col-md-4 mb-2">
              <div class="btn-group btn-group-sm mr-2 mb-2">
                <button
                  class="btn btn-outline-primary"
                  @click="autoClose"
                  :disabled="loading"
                >
                  Auto-close hôm nay
                </button>
              </div>

              <div class="btn-group btn-group-sm mb-2">
                <button
                  class="btn btn-primary dropdown-toggle"
                  type="button"
                  data-toggle="dropdown"
                  :disabled="loading"
                >
                  Đóng ca hàng loạt
                </button>
                <div class="dropdown-menu">
                  <a
                    class="dropdown-item"
                    href="#"
                    @click.prevent="bulkClose('ALL')"
                  >
                    Toàn công ty
                  </a>
                  <a
                    class="dropdown-item"
                    href="#"
                    @click.prevent="bulkClose('DEPT')"
                    :class="{ disabled: !filter.phongBanId }"
                  >
                    Theo phòng ban
                  </a>
                  <a
                    class="dropdown-item"
                    href="#"
                    @click.prevent="bulkClose('USERS')"
                    :class="{ disabled: selectedUserIds.length === 0 }"
                  >
                    Theo NV đã chọn ({{ selectedUserIds.length }})
                  </a>
                </div>
              </div>
            </div>
            <div class="col-md-5 mb-2 text-muted small">
              Chọn nhân viên bằng checkbox đầu dòng để đóng ca theo NV.
              <br>
              <span class="font-italic">
                Dữ liệu chấm công đã chuẩn hoá theo BE (số phút trễ/sớm, nghỉ phép, lệnh, v.v.).
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Stats -->
      <div class="card mb-3" v-if="matrix">
        <div class="card-body py-3">
          <div class="row">
            <!-- Today -->
            <div class="col-lg-6 mb-3 mb-lg-0">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <div class="font-weight-600">
                  Hôm nay
                  <span class="text-muted small">({{ todayStr() }})</span>
                </div>
                <span
                  v-if="!isCurrentMonth"
                  class="badge badge-light small text-muted"
                >
                  Ngoài tháng đang xem
                </span>
              </div>
              <div class="d-flex flex-wrap stats-row">
                <div class="mini-stat">
                  <div class="mini-stat-label">Đi làm (người)</div>
                  <div class="mini-stat-value">{{ fmt(summaryToday.presentUsers) }}</div>
                </div>
                <div class="mini-stat">
                  <div class="mini-stat-label">Nghỉ phép (người)</div>
                  <div class="mini-stat-value">{{ fmt(summaryToday.leaveUsers) }}</div>
                </div>
                <div class="mini-stat">
                  <div class="mini-stat-label">Đi trễ (người)</div>
                  <div class="mini-stat-value">{{ fmt(summaryToday.lateUsers) }}</div>
                </div>
                <div class="mini-stat">
                  <div class="mini-stat-label">Vào sớm (người)</div>
                  <div class="mini-stat-value">{{ fmt(summaryToday.vaoSomUsers) }}</div>
                </div>
                <div class="mini-stat">
                  <div class="mini-stat-label">Về sớm (người)</div>
                  <div class="mini-stat-value">{{ fmt(summaryToday.raSomUsers) }}</div>
                </div>
              </div>
            </div>

            <!-- Month -->
            <div class="col-lg-6">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <div class="font-weight-600">
                  Tổng quan {{ ymLabel }}
                </div>
                <span class="badge badge-light small text-muted">
                  {{ fmt(summaryMonth.employeeCount) }} NV ·
                  {{ summaryMonth.workingDays }} ngày
                </span>
              </div>
              <div class="d-flex flex-wrap stats-row">
                <div class="mini-stat">
                  <div class="mini-stat-label">Tổng công</div>
                  <div class="mini-stat-value">
                    {{ summaryMonth.totalCongAll.toFixed(2) }}
                  </div>
                </div>
                <div class="mini-stat">
                  <div class="mini-stat-label">NV có công</div>
                  <div class="mini-stat-value">
                    {{ fmt(summaryMonth.presentUsers) }}
                  </div>
                </div>
                <div class="mini-stat">
                  <div class="mini-stat-label">NV từng nghỉ phép</div>
                  <div class="mini-stat-value">
                    {{ fmt(summaryMonth.leaveUsers) }}
                  </div>
                </div>
                <div class="mini-stat">
                  <div class="mini-stat-label">NV từng đi trễ</div>
                  <div class="mini-stat-value">
                    {{ fmt(summaryMonth.lateUsers) }}
                  </div>
                </div>
                <div class="mini-stat">
                  <div class="mini-stat-label">NV từng vào sớm</div>
                  <div class="mini-stat-value">
                    {{ fmt(summaryMonth.vaoSomUsers) }}
                  </div>
                </div>
                <div class="mini-stat">
                  <div class="mini-stat-label">NV từng về sớm</div>
                  <div class="mini-stat-value">
                    {{ fmt(summaryMonth.raSomUsers) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Matrix -->
      <div v-if="matrix" class="card">
        <div class="card-header d-flex justify-content-between align-items-center py-2">
          <div class="font-weight-600">
            Bảng chấm công tháng {{ ymLabel }}
            <span class="text-muted small ml-2">
              Bộ phận: {{ departmentLabel() }}
            </span>
          </div>
          <div class="small text-muted legend-wrap">
            <span class="legend-item mr-2">
              <i class="fa fa-check text-success mr-1"></i> Có mặt
            </span>
            <span class="legend-item mr-2">
              <i class="fa fa-times text-danger mr-1"></i> Nghỉ/vắng
            </span>
            <span class="legend-item mr-2">
              <i class="fa fa-sign-out text-warning mr-1"></i> Về sớm
            </span>
            <span class="legend-item mr-2">
              <i class="fa fa-clock-o text-warning mr-1"></i> Ca mở
            </span>
            <span class="legend-item mr-2">
              <span class="legend-dot legend-leave"></span> Nghỉ phép
            </span>
            <span class="legend-item mr-2">
              <span class="legend-dot legend-holiday"></span> Nghỉ lễ
            </span>
            <span class="legend-item">
              <span class="legend-dot legend-lenh"></span> Có lệnh
            </span>
          </div>
        </div>

        <div class="card-body p-0">
          <div class="attendance-table-wrapper">
            <table class="table table-sm table-hover custom-table attendance-table mb-0">
              <thead>
                <tr>
                  <th class="sticky-col-left" style="width: 36px;">
                    <input
                      type="checkbox"
                      v-model="selectAll"
                      @change="toggleSelectAll"
                    >
                  </th>
                  <th class="sticky-col-left name-col">
                    Nhân viên
                  </th>
                  <th
                    v-for="hd in headerDays"
                    :key="'h' + hd.day"
                    class="day-col head-day"
                    :class="{ 'sun-head': hd.isSun }"
                  >
                    <div class="d-flex flex-column align-items-center">
                      <span class="dow">{{ hd.dowShort }}</span>
                      <span class="daynum">{{ hd.day }}</span>
                    </div>
                  </th>
                  <th class="text-right total-col">
                    Tổng công
                  </th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="row in matrix.rows" :key="row.userId">
                  <td class="sticky-col-left">
                    <input
                      type="checkbox"
                      :value="row.userId"
                      v-model="selectedUserIds"
                    >
                  </td>
                  <td class="sticky-col-left name-col">
                    <div class="d-flex align-items-center">
                      <span class="avatar avatar-xs mr-2">
                        <img
                          v-if="row.avatarUrl"
                          :src="row.avatarUrl"
                          alt=""
                        >
                        <span v-else class="avatar-fallback">
                          {{ initials(row.hoTen) }}
                        </span>
                      </span>
                      <div class="truncate-name">
                        <button
                          type="button"
                          class="btn btn-link btn-xs p-0 font-weight-600 text-truncate user-name-btn"
                          @click="openUserDetail(row)"
                          :title="'Xem chi tiết ' + row.hoTen"
                        >
                          {{ row.hoTen }}
                        </button>
                        <div class="small text-muted">
                          {{ row.phongBan || "—" }}
                          <span class="text-monospace">
                            (#{{ row.userId }})
                          </span>
                        </div>
                        <div class="mt-1">
                          <span
                            class="badge badge-pill badge-light border text-muted badge-compact"
                            v-if="row.chamCongMode"
                          >
                            {{ row.chamCongMode === "NGAY_2x"
                              ? "Chấm theo ngày (2x)"
                              : "Chấm theo buổi (2x2)" }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>

                  <td
                    v-for="cell in row.daily"
                    :key="row.userId + '-' + cell.day"
                    class="day-col day-cell"
                    :class="dayCellClasses(cell)"
                    :title="tooltipCell(row, cell)"
                    @click="openDay(row, cell)"
                  >
                    <!-- View: ICON -->
                    <template v-if="viewMode === 'icon'">
                      <!-- Mode 2x2: 4 icon -->
                      <template v-if="row.chamCongMode === 'BUOI_2x2'">
                        <div class="quad-day">
                          <span class="quad-slot" title="Sáng - Vào">
                            <i :class="iconQuarter(cell, 'AM_IN')"></i>
                          </span>
                          <span class="quad-slot" title="Sáng - Ra">
                            <i :class="iconQuarter(cell, 'AM_OUT')"></i>
                          </span>
                          <span class="quad-slot" title="Chiều - Vào">
                            <i :class="iconQuarter(cell, 'PM_IN')"></i>
                          </span>
                          <span class="quad-slot" title="Chiều - Ra">
                            <i :class="iconQuarter(cell, 'PM_OUT')"></i>
                          </span>
                        </div>
                      </template>
                      <!-- Mode 2x: 2 icon (S/C) -->
                      <template v-else>
                        <div class="half-day">
                          <span class="half-slot" title="Sáng">
                            <i :class="iconHalf(cell, 'AM')"></i>
                          </span>
                          <span class="half-slot" title="Chiều">
                            <i :class="iconHalf(cell, 'PM')"></i>
                          </span>
                        </div>
                      </template>
                    </template>

                    <!-- View: Số công -->
                    <template v-else>
                      <div class="cong-box" :class="congClass(cellCong(cell))">
                        {{ cellCong(cell).toFixed(2) }}
                      </div>
                    </template>
                  </td>

                  <td class="text-right total-col">
                    <span class="badge badge-soft-primary">
                      {{ (row.totalCong ?? sumRowCong(row)).toFixed(2) }}
                    </span>
                  </td>
                </tr>

                <tr v-if="matrix.rows.length === 0">
                  <td
                    :colspan="(matrix.daysInMonth + 3)"
                    class="text-center text-muted py-4"
                  >
                    Không có dữ liệu
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div
        v-else-if="!loading"
        class="text-muted text-center py-5"
      >
        Chưa có dữ liệu chấm công cho tháng này.
      </div>
    </div>

    <!-- Modal chi tiết nhân viên -->
    <div
      class="modal custom-modal fade show"
      v-if="userModal.open"
      style="display: block;"
      role="dialog"
      aria-modal="true"
    >
      <div
        class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-xl"
        role="document"
      >
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              Chi tiết chấm công — {{ userModal.row?.hoTen }} ({{ ymLabel }})
            </h5>
            <button type="button" class="close" @click="closeUserModal">
              <span>&times;</span>
            </button>
          </div>

          <div class="modal-body" v-if="userModal.row">
            <div class="mb-3 d-flex justify-content-between align-items-center">
              <div class="small text-muted">
                Phòng ban:
                <strong>{{ userModal.row.phongBan || "—" }}</strong>
                · ID:
                <span class="text-monospace">
                  #{{ userModal.row.userId }}
                </span>
              </div>
              <button
                class="btn btn-sm btn-success"
                @click="exportExcelUser(userModal.row)"
              >
                <i class="fa fa-file-excel-o mr-1"></i>
                Xuất Excel cá nhân
              </button>
            </div>

            <div class="row mb-3">
              <div class="col-md-12">
                <div class="d-flex flex-wrap stats-row">
                  <div class="mini-stat">
                    <div class="mini-stat-label">Tổng công</div>
                    <div class="mini-stat-value">
                      {{ userStats.totalCong.toFixed(2) }}
                    </div>
                  </div>
                  <div class="mini-stat">
                    <div class="mini-stat-label">Phút đi trễ</div>
                    <div class="mini-stat-value">
                      {{ fmt(userStats.totalMinutesVaoTre) }}
                    </div>
                  </div>
                  <div class="mini-stat">
                    <div class="mini-stat-label">Phút vào sớm</div>
                    <div class="mini-stat-value">
                      {{ fmt(userStats.totalMinutesVaoSom) }}
                    </div>
                  </div>
                  <div class="mini-stat">
                    <div class="mini-stat-label">Phút về sớm</div>
                    <div class="mini-stat-value">
                      {{ fmt(userStats.totalMinutesRaSom) }}
                    </div>
                  </div>
                  <div class="mini-stat">
                    <div class="mini-stat-label">Quên chấm (lần)</div>
                    <div class="mini-stat-value">
                      {{ fmt(userStats.totalQuenCham) }}
                    </div>
                  </div>
                  <div class="mini-stat">
                    <div class="mini-stat-label">Nghỉ phép</div>
                    <div class="mini-stat-value">
                      {{ fmt(userStats.totalNghiPhepFull) }}N ·
                      {{ fmt(userStats.totalNghiPhepAm) }}S ·
                      {{ fmt(userStats.totalNghiPhepPm) }}C
                    </div>
                  </div>
                  <div class="mini-stat">
                    <div class="mini-stat-label">Lệnh hoàn thành</div>
                    <div class="mini-stat-value">
                      {{ fmt(userStats.totalLenhHoanThanh) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="table-responsive" style="max-height: 50vh;">
              <table class="table table-sm table-bordered mb-0">
                <thead>
                  <tr>
                    <th>Ngày</th>
                    <th>Thứ</th>
                    <th class="text-center">Công</th>
                    <th class="text-center">Phút đi trễ</th>
                    <th class="text-center">Phút vào sớm</th>
                    <th class="text-center">Phút về sớm</th>
                    <th class="text-center">Quên chấm</th>
                    <th>Nghỉ phép</th>
                    <th class="text-center">Số lệnh</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="d in userDays" :key="d.day">
                    <td>{{ d.dateLabel }}</td>
                    <td>{{ d.dow }}</td>
                    <td class="text-center">{{ d.cong.toFixed(2) }}</td>
                    <td class="text-center">{{ d.vaoTre }}</td>
                    <td class="text-center">{{ d.vaoSom }}</td>
                    <td class="text-center">{{ d.raSom }}</td>
                    <td class="text-center">{{ d.quen }}</td>
                    <td>{{ d.leaveDisplay }}</td>
                    <td class="text-center">{{ d.lenh }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="modal-body p-4" v-else>
            Đang tải…
          </div>
        </div>
      </div>
    </div>

    <!-- Modal ngày -->
    <div
      class="modal custom-modal fade show"
      v-if="dayModal.open"
      style="display: block;"
      role="dialog"
      aria-modal="true"
    >
      <div
        class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg"
        role="document"
      >
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              {{ dayModal.row?.hoTen }} — {{ formatDateHuman(dayModal.date) }}
            </h5>
            <button type="button" class="close" @click="closeDayModal">
              <span>&times;</span>
            </button>
          </div>

          <div class="modal-body" v-if="dayModal.detail">
            <div class="row">
              <div class="col-md-6">
                <div class="card punch-status mb-3">
                  <div class="card-body">
                    <h5 class="card-title mb-3">
                      Tổng hợp
                      <small class="text-muted">
                        {{ formatDateHuman(dayModal.detail.ngay) }}
                      </small>
                    </h5>

                    <div class="punch-det">
                      <h6>Vào sớm nhất</h6>
                      <p class="mb-1">
                        {{
                          dayModal.detail.gioVaoTong
                            ? formatDT(dayModal.detail.gioVaoTong)
                            : "—"
                        }}
                      </p>
                    </div>

                    <div class="punch-info my-2">
                      <div class="punch-hours">
                        <span class="font-weight-600">
                          {{ (dayModal.detail.soCong ?? 0).toFixed(2) }} công
                        </span>
                      </div>
                    </div>

                    <div class="punch-det mb-1">
                      <h6>Ra muộn nhất</h6>
                      <p class="mb-1">
                        {{
                          dayModal.detail.gioRaTong
                            ? formatDT(dayModal.detail.gioRaTong)
                            : "—"
                        }}
                      </p>
                    </div>

                    <div
                      class="small text-muted mt-2"
                      v-if="dayModal.detail.tongPhutVaoTre || dayModal.detail.tongPhutRaSom"
                    >
                      Đi trễ {{ dayModal.detail.tongPhutVaoTre || 0 }} phút,
                      về sớm {{ dayModal.detail.tongPhutRaSom || 0 }} phút.
                    </div>
                  </div>
                </div>
              </div>

              <!-- Hoạt động -->
              <div class="col-md-6">
                <div class="card recent-activity mb-3">
                  <div class="card-body">
                    <h5 class="card-title mb-3">Hoạt động trong ngày</h5>
                    <ul class="res-activity-list">
                      <li v-for="it in modalActivities" :key="it.key">
                        <p class="mb-0">
                          {{ it.label }}
                        </p>
                        <p class="res-activity-time mb-1">
                          <i class="fa fa-clock-o mr-1"></i>
                          {{ it.time }}
                          <span
                            v-if="it.extra"
                            class="text-muted"
                          >
                            — {{ it.extra }}
                          </span>
                        </p>
                      </li>
                      <li
                        v-if="modalActivities.length === 0"
                        class="text-muted"
                      >
                        Không có hoạt động
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <!-- Ảnh -->
            <div
              class="row"
              v-if="dayModal.detail.chiTietBuoi?.length"
            >
              <div
                class="col-md-6"
                v-for="b in dayModal.detail.chiTietBuoi"
                :key="'b' + b.idCcb"
              >
                <div class="card mb-3">
                  <div class="card-body">
                    <div class="font-weight-600 mb-2">
                      Ca {{ b.buoi === "SANG" ? "Sáng" : "Chiều" }}
                    </div>
                    <div class="small mb-1">
                      Vào:
                      {{ b.gioVao ? formatDT(b.gioVao) : "—" }}
                      ({{ b.vaoTrangThai || "—" }})
                    </div>
                    <div class="small mb-2">
                      Ra:
                      {{ b.gioRa ? formatDT(b.gioRa) : "—" }}
                      ({{ b.raTrangThai || "—" }})
                    </div>

                    <div class="d-flex flex-wrap">
                      <img
                        v-if="b.anhVaoUrl"
                        :src="b.anhVaoUrl"
                        class="img-thumb mr-2 mb-2"
                        @click="openLightbox([b.anhVaoUrl, b.anhRaUrl].filter(Boolean), 0)"
                        alt="Ảnh vào ca"
                      >
                      <img
                        v-if="b.anhRaUrl"
                        :src="b.anhRaUrl"
                        class="img-thumb mb-2"
                        @click="openLightbox([b.anhVaoUrl, b.anhRaUrl].filter(Boolean), b.anhVaoUrl ? 1 : 0)"
                        alt="Ảnh ra ca"
                      >
                    </div>

                    <div
                      class="small text-muted mt-2"
                      v-if="b.diaDiemVaoText"
                    >
                      Vào: {{ b.diaDiemVaoText }}
                    </div>
                    <div
                      class="small text-muted"
                      v-if="b.diaDiemRaText"
                    >
                      Ra: {{ b.diaDiemRaText }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Sửa công / giờ -->
            <div class="card">
              <div class="card-body">
                <h5 class="card-title mb-3">
                  Sửa công / giờ trong ngày
                </h5>
                <div class="form-row">
                  <div class="form-group col-md-3">
                    <label class="form-label-sm">Số công (override)</label>
                    <input
                      type="text"
                      inputmode="decimal"
                      class="form-control form-control-sm"
                      v-model.trim="adjustForm.soCongStr"
                      placeholder="0.30 / 0,30"
                    >
                    <div class="btn-group btn-group-sm mt-1">
                      <button
                        class="btn btn-light"
                        @click="adjustForm.soCongStr = '0'"
                      >
                        0
                      </button>
                      <button
                        class="btn btn-light"
                        @click="adjustForm.soCongStr = '0.5'"
                      >
                        0.5
                      </button>
                      <button
                        class="btn btn-light"
                        @click="adjustForm.soCongStr = '1'"
                      >
                        1.0
                      </button>
                    </div>
                  </div>

                  <div class="form-group col-md-3">
                    <label class="form-label-sm">Vào sáng</label>
                    <input
                      type="time"
                      class="form-control form-control-sm"
                      v-model="adjustForm.sangVaoTime"
                    >
                  </div>
                  <div class="form-group col-md-3">
                    <label class="form-label-sm">Ra sáng</label>
                    <input
                      type="time"
                      class="form-control form-control-sm"
                      v-model="adjustForm.sangRaTime"
                    >
                  </div>
                  <div class="form-group col-md-3">
                    <label class="form-label-sm">Vào chiều</label>
                    <input
                      type="time"
                      class="form-control form-control-sm"
                      v-model="adjustForm.chieuVaoTime"
                    >
                  </div>
                  <div class="form-group col-md-3">
                    <label class="form-label-sm">Ra chiều</label>
                    <input
                      type="time"
                      class="form-control form-control-sm"
                      v-model="adjustForm.chieuRaTime"
                    >
                  </div>
                </div>

                <button
                  class="btn btn-warning btn-sm"
                  :disabled="savingAdjust"
                  @click="saveAdjust"
                >
                  Lưu chỉnh sửa
                </button>
              </div>
            </div>
          </div>

          <div class="modal-body p-4" v-else>
            Đang tải…
          </div>
        </div>
      </div>
    </div>

    <!-- Backdrop chung cho 2 modal -->
    <div
      v-if="dayModal.open || userModal.open"
      class="modal-backdrop fade show"
    ></div>

    <!-- Lightbox -->
    <vue-easy-lightbox
      :visible="lightboxVisible"
      :imgs="lightboxImgs"
      :index="lightboxIndex"
      @hide="onHideLightbox"
    />
  </div>
</template>

<script>
import api from "../api";
import Swal from "sweetalert2";
import XLSX from "xlsx-js-style";
import VueEasyLightbox from "vue-easy-lightbox";

export default {
  name: "AttendanceAdmin",
  components: { VueEasyLightbox },
  data() {
    const now = new Date();
    const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
      now.getDate()
    ).padStart(2, "0")}`;
    return {
      loading: false,
      savingAdjust: false,
      matrix: null,
      phongBans: [],
      viewMode: "cong",

      filter: { keyword: "", month: ym, phongBanId: null },
      selectedUserIds: [],
      selectAll: false,
      closeForm: { date: todayStr },

      dayModal: { open: false, row: null, date: null, detail: null },
      userModal: { open: false, row: null },

      adjustForm: {
        soCongStr: "",
        sangVaoTime: "",
        sangRaTime: "",
        chieuVaoTime: "",
        chieuRaTime: ""
      },

      lightboxVisible: false,
      lightboxImgs: [],
      lightboxIndex: 0
    };
  },
  computed: {
    ymLabel() {
      if (!this.filter.month) return "";
      const [y, m] = this.filter.month.split("-").map(Number);
      return `Tháng ${m}/${y}`;
    },
    isCurrentMonth() {
      if (!this.filter.month) return false;
      const [y, m] = this.filter.month.split("-").map(Number);
      const now = new Date();
      return y === now.getFullYear() && m === now.getMonth() + 1;
    },
    headerDays() {
      const arr = [];
      if (!this.matrix || !this.filter.month) return arr;
      const [y, m] = this.filter.month.split("-").map(Number);
      for (let d = 1; d <= this.matrix.daysInMonth; d++) {
        const dt = new Date(y, m - 1, d);
        arr.push({
          day: d,
          dowShort: dt.toLocaleDateString("vi-VN", { weekday: "short" }),
          isSun: dt.getDay() === 0
        });
      }
      return arr;
    },
    summaryToday() {
      const res = {
        presentUsers: 0,
        leaveUsers: 0,
        lateUsers: 0,
        vaoSomUsers: 0,
        raSomUsers: 0
      };
      if (!this.matrix || !this.isCurrentMonth) return res;
      const today = new Date().getDate();

      const present = new Set();
      const leave = new Set();
      const late = new Set();
      const vaoSom = new Set();
      const raSom = new Set();

      (this.matrix.rows || []).forEach(r => {
        const cell = (r.daily || []).find(c => c.day === today);
        if (!cell) return;

        const hasPresent = cell.amPresent === true || cell.pmPresent === true;
        const hasOpen = cell.amOpen === true || cell.pmOpen === true;
        const hasLeave =
          cell.coNghiPhep === true ||
          cell.nghiPhepFull === true ||
          cell.nghiPhepAm === true ||
          cell.nghiPhepPm === true;

        const vt = this.numOrNull(cell.tongVaoTre) || 0;
        const vs = this.numOrNull(cell.tongVaoSom) || 0;
        const rs = this.numOrNull(cell.tongRaSom) || 0;

        if (hasPresent) present.add(r.userId);
        if (!hasPresent && !hasOpen && hasLeave) leave.add(r.userId);
        if (vt > 0) late.add(r.userId);
        if (vs > 0) vaoSom.add(r.userId);
        if (rs > 0) raSom.add(r.userId);
      });

      res.presentUsers = present.size;
      res.leaveUsers = leave.size;
      res.lateUsers = late.size;
      res.vaoSomUsers = vaoSom.size;
      res.raSomUsers = raSom.size;
      return res;
    },
    summaryMonth() {
      const res = {
        totalCongAll: 0,
        employeeCount: this.matrix ? (this.matrix.rows || []).length : 0,
        workingDays: this.matrix ? this.matrix.daysInMonth || 0 : 0,
        presentUsers: 0,
        leaveUsers: 0,
        lateUsers: 0,
        vaoSomUsers: 0,
        raSomUsers: 0
      };
      if (!this.matrix) return res;

      const present = new Set();
      const leave = new Set();
      const late = new Set();
      const vaoSom = new Set();
      const raSom = new Set();

      (this.matrix.rows || []).forEach(r => {
        let hasAnyPresent = false;
        let hasAnyLeave = false;
        let hasLate = false;
        let hasVaoSom = false;
        let hasRaSom = false;

        (r.daily || []).forEach(c => {
          const cong = this.numOrNull(c.soCong != null ? c.soCong : c.congNgay);
          if (cong !== null) res.totalCongAll += cong;

          const hasPresentDay = c.amPresent === true || c.pmPresent === true;
          const hasLeaveDay =
            c.coNghiPhep === true ||
            c.nghiPhepFull === true ||
            c.nghiPhepAm === true ||
            c.nghiPhepPm === true;

          if (hasPresentDay) hasAnyPresent = true;
          if (hasLeaveDay) hasAnyLeave = true;

          if (this.numOrNull(c.tongVaoTre) > 0) hasLate = true;
          if (this.numOrNull(c.tongVaoSom) > 0) hasVaoSom = true;
          if (this.numOrNull(c.tongRaSom) > 0) hasRaSom = true;
        });

        if (hasAnyPresent) present.add(r.userId);
        if (hasAnyLeave) leave.add(r.userId);
        if (hasLate) late.add(r.userId);
        if (hasVaoSom) vaoSom.add(r.userId);
        if (hasRaSom) raSom.add(r.userId);
      });

      res.totalCongAll = +res.totalCongAll.toFixed(2);
      res.presentUsers = present.size;
      res.leaveUsers = leave.size;
      res.lateUsers = late.size;
      res.vaoSomUsers = vaoSom.size;
      res.raSomUsers = raSom.size;
      return res;
    },
    modalActivities() {
      const d = this.dayModal.detail;
      if (!d?.chiTietBuoi) return [];
      const items = [];
      d.chiTietBuoi.forEach(b => {
        if (b.gioVao) {
          items.push({
            key: `in-${b.idCcb}`,
            label: b.buoi === "SANG" ? "Vào ca sáng" : "Vào ca chiều",
            time: this.formatTime(b.gioVao),
            extra: b.vaoTrangThai
          });
        }
        if (b.gioRa) {
          items.push({
            key: `out-${b.idCcb}`,
            label: b.buoi === "SANG" ? "Ra ca sáng" : "Ra ca chiều",
            time: this.formatTime(b.gioRa),
            extra: b.raTrangThai
          });
        }
      });
      return items.sort((a, b) => a.time.localeCompare(b.time));
    },
    userStats() {
      if (!this.userModal.row || !this.matrix) {
        return {
          totalCong: 0,
          totalMinutesVaoTre: 0,
          totalMinutesVaoSom: 0,
          totalMinutesRaSom: 0,
          totalQuenCham: 0,
          totalNghiPhepFull: 0,
          totalNghiPhepAm: 0,
          totalNghiPhepPm: 0,
          totalLenhHoanThanh: 0
        };
      }
      const row =
        (this.matrix.rows || []).find(r => r.userId === this.userModal.row.userId) ||
        this.userModal.row;
      return this.calcRowStats(row);
    },
    userDays() {
      if (!this.userModal.row || !this.matrix || !this.filter.month) return [];
      const row =
        (this.matrix.rows || []).find(r => r.userId === this.userModal.row.userId) ||
        this.userModal.row;
      const [y, m] = this.filter.month.split("-").map(Number);
      const maxDay = this.matrix.daysInMonth || 0;
      const result = [];
      for (let d = 1; d <= maxDay; d++) {
        const dt = new Date(y, m - 1, d);
        const cell = (row.daily || []).find(c => c.day === d) || {};

        const cong = this.cellCong(cell);
        const vaoTre = this.numOrNull(cell.tongVaoTre) || 0;
        const vaoSom = this.numOrNull(cell.tongVaoSom) || 0;
        const raSom = this.numOrNull(cell.tongRaSom) || 0;
        const quen = this.numOrNull(cell.tongQuenCham) || 0;

        const leaveFlags = {
          full: !!cell.nghiPhepFull,
          am: !!cell.nghiPhepAm,
          pm: !!cell.nghiPhepPm
        };
        let leaveDisplay = "";
        if (leaveFlags.full) leaveDisplay = "Nghỉ phép (cả ngày)";
        else if (leaveFlags.am && leaveFlags.pm) leaveDisplay = "Nghỉ phép (S+C)";
        else if (leaveFlags.am) leaveDisplay = "Nghỉ phép sáng";
        else if (leaveFlags.pm) leaveDisplay = "Nghỉ phép chiều";

        const lenh = this.numOrNull(cell.soLenhHoanThanh) || 0;

        result.push({
          day: d,
          dateLabel: dt.toLocaleDateString("vi-VN", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric"
          }),
          dow: dt.toLocaleDateString("vi-VN", { weekday: "short" }),
          cong,
          vaoTre,
          vaoSom,
          raSom,
          quen,
          leaveDisplay,
          lenh
        });
      }
      return result;
    }
  },
  mounted() {
    this.fetchPhongBan();
    this.fetchMatrix();
  },
  methods: {
    numOrNull(val) {
      if (val === null || val === undefined) return null;
      const s = String(val).replace(",", ".").trim();
      if (s === "") return null;
      const n = Number(s);
      return Number.isFinite(n) ? n : null;
    },
    cellCong(cell) {
      const v = this.numOrNull(
        cell?.soCong != null
          ? cell.soCong
          : cell?.congNgay != null
          ? cell.congNgay
          : cell?.totalCong
      );
      if (v !== null) return +v.toFixed(2);

      const am = this.numOrNull(cell?.soCongAm);
      const pm = this.numOrNull(cell?.soCongPm);
      if (am !== null || pm !== null) {
        return +(((am || 0) + (pm || 0))).toFixed(2);
      }

      let cong = 0;
      if (cell.amPresent === true) cong += 0.5;
      if (cell.pmPresent === true) cong += 0.5;
      return +cong.toFixed(2);
    },
    sumRowCong(row) {
      return +((row.daily || []).reduce((s, c) => s + this.cellCong(c), 0)).toFixed(2);
    },
    congClass(v) {
      if (v >= 1) return "cong-strong";
      if (v > 0) return "cong-mid";
      return "cong-zero";
    },
    isSunday(day) {
      if (!this.filter.month) return false;
      const [y, m] = this.filter.month.split("-").map(Number);
      return new Date(y, m - 1, day).getDay() === 0;
    },
    departmentLabel() {
      if (!this.filter.phongBanId) return "TẤT CẢ PHÒNG BAN";
      const pb = this.phongBans.find(p => p.idPhongBan === this.filter.phongBanId);
      return pb?.tenPhongBan || "TẤT CẢ PHÒNG BAN";
    },
    dayCellClasses(cell) {
      const classes = {};
      if (cell.sunday || this.isSunday(cell.day)) classes["sunday-cell"] = true;
      if (cell.holiday) classes["holiday-cell"] = true;
      if (cell.coNghiPhep || cell.nghiPhepFull || cell.nghiPhepAm || cell.nghiPhepPm)
        classes["leave-cell"] = true;
      if (this.numOrNull(cell.soLenhHoanThanh) > 0) classes["lenh-cell"] = true;

      if (this.isCurrentMonth) {
        const today = new Date().getDate();
        if (cell.day === today) classes["today-cell"] = true;
      }
      return classes;
    },
    tooltipCell(row, cell) {
      const cong = this.cellCong(cell).toFixed(2);
      const [y, m] = this.filter.month.split("-").map(Number);
      const dt = new Date(y, m - 1, cell.day);
      const dstr = dt.toLocaleDateString("vi-VN", {
        weekday: "short",
        day: "2-digit",
        month: "2-digit"
      });

      const amCongVal = this.numOrNull(cell.soCongAm);
      const pmCongVal = this.numOrNull(cell.soCongPm);
      const amCong = amCongVal !== null ? amCongVal.toFixed(2) : "0.00";
      const pmCong = pmCongVal !== null ? pmCongVal.toFixed(2) : "0.00";

      const fmtHalfSummary = (label, present, vaoTre, vaoSom, raSom, quenCham, hasLeave) => {
        const tokens = [];
        if (present) tokens.push("có mặt");
        else if (hasLeave) tokens.push("nghỉ phép");
        else tokens.push("vắng");
        if (vaoTre > 0) tokens.push(`trễ ${vaoTre}′`);
        if (vaoSom > 0) tokens.push(`vào sớm ${vaoSom}′`);
        if (raSom > 0) tokens.push(`về sớm ${raSom}′`);
        if (quenCham) tokens.push("quên chấm");
        return `${label}: ${tokens.join(", ")}`;
      };

      const amLine = fmtHalfSummary(
        "Sáng",
        cell.amPresent === true,
        this.numOrNull(cell.amVaoTre) || 0,
        this.numOrNull(cell.amVaoSom) || 0,
        this.numOrNull(cell.amRaSom) || 0,
        cell.amQuenCham === true,
        cell.nghiPhepAm === true || cell.nghiPhepFull === true
      );
      const pmLine = fmtHalfSummary(
        "Chiều",
        cell.pmPresent === true,
        this.numOrNull(cell.pmVaoTre) || 0,
        this.numOrNull(cell.pmVaoSom) || 0,
        this.numOrNull(cell.pmRaSom) || 0,
        cell.pmQuenCham === true,
        cell.nghiPhepPm === true || cell.nghiPhepFull === true
      );

      const parts = [];
      parts.push(`${row.hoTen} — ${dstr}`);
      parts.push(`Công ngày: ${cong} (Sáng ${amCong}, Chiều ${pmCong})`);
      parts.push(amLine);
      parts.push(pmLine);

      if (cell.holiday) parts.push("Ngày nghỉ lễ");
      if (cell.soLenhHoanThanh) parts.push(`Lệnh hoàn thành: ${cell.soLenhHoanThanh}`);

      return parts.join("\n");
    },
    async fetchPhongBan() {
      try {
        const rs = await api.get("/api/phong-ban/options");
        this.phongBans = rs?.data?.data || [];
      } catch {
        try {
          const rs = await api.get("/api/phong-ban", { params: { page: 0, size: 100 } });
          this.phongBans = (rs?.data?.data?.content || []).map(x => ({
            idPhongBan: x.idPhongBan,
            tenPhongBan: x.tenPhongBan
          }));
        } catch {
          this.phongBans = [];
        }
      }
    },
    postProcessMatrix() {
      if (!this.matrix) return;
      (this.matrix.rows || []).forEach(r => {
        r.totalCong = this.numOrNull(r.totalCong) || 0;
        (r.daily || []).forEach(c => {
          const cong = this.numOrNull(c.soCong != null ? c.soCong : c.congNgay);
          c.soCong = cong !== null ? +cong.toFixed(2) : 0;
          const am = this.numOrNull(c.soCongAm);
          const pm = this.numOrNull(c.soCongPm);
          c.soCongAm = am !== null ? +am.toFixed(2) : 0;
          c.soCongPm = pm !== null ? +pm.toFixed(2) : 0;

          c.tongVaoTre = this.numOrNull(c.tongVaoTre) || 0;
          c.tongVaoSom = this.numOrNull(c.tongVaoSom) || 0;
          c.tongRaSom = this.numOrNull(c.tongRaSom) || 0;
          c.tongQuenCham = this.numOrNull(c.tongQuenCham) || 0;
          c.soLenhHoanThanh = this.numOrNull(c.soLenhHoanThanh) || 0;
        });
      });
    },
    async fetchMatrix() {
      this.loading = true;
      try {
        const params = { month: this.filter.month };
        if (this.filter.keyword) params.keyword = this.filter.keyword;
        if (this.filter.phongBanId) params.phongBanId = this.filter.phongBanId;

        const { data } = await api.get("/api/cham-cong/admin/matrix", { params });
        this.matrix = data?.data || null;
        this.selectedUserIds = [];
        this.selectAll = false;
        this.postProcessMatrix();
      } catch (e) {
        console.error(e);
        this.matrix = null;
        this.alertUser(e?.response?.data?.message || "Không tải được dữ liệu chấm công", "error");
      } finally {
        this.loading = false;
      }
    },
    async autoClose() {
      const cfm = await Swal.fire({
        icon: "question",
        title: "Auto-close hôm nay?",
        showCancelButton: true,
        confirmButtonText: "Thực hiện",
        cancelButtonText: "Hủy"
      });
      if (!cfm.isConfirmed) return;
      this.loading = true;
      try {
        await api.post("/api/cham-cong/auto-close");
        await this.fetchMatrix();
        this.toastOk("Đã auto-close");
      } catch (e) {
        console.error(e);
        this.alertUser(e?.response?.data?.message || "Auto-close lỗi", "error");
      } finally {
        this.loading = false;
      }
    },
    async bulkClose(scope) {
      const payload = {
        scope,
        date: this.closeForm.date || this.todayStr(),
        phongBanId: scope === "DEPT" ? this.filter.phongBanId : null,
        userIds: scope === "USERS" ? this.selectedUserIds : null
      };
      const cfm = await Swal.fire({
        icon: "warning",
        title: "Xác nhận đóng ca hàng loạt",
        text: `Phạm vi: ${scope}`,
        showCancelButton: true,
        confirmButtonText: "Đóng ca",
        cancelButtonText: "Hủy"
      });
      if (!cfm.isConfirmed) return;
      this.loading = true;
      try {
        await api.post("/api/cham-cong/admin/close-sessions", payload);
        await this.fetchMatrix();
        this.toastOk("Đã đóng ca");
      } catch (e) {
        console.error(e);
        this.alertUser(e?.response?.data?.message || "Đóng ca lỗi", "error");
      } finally {
        this.loading = false;
      }
    },
    openDay(row, cell) {
      const [y, m] = this.filter.month.split("-").map(Number);
      const date = `${y}-${String(m).padStart(2, "0")}-${String(cell.day).padStart(2, "0")}`;
      this.dayModal = { open: true, row, date, detail: null };
      this.adjustForm = {
        soCongStr: "",
        sangVaoTime: "",
        sangRaTime: "",
        chieuVaoTime: "",
        chieuRaTime: ""
      };
      this.fetchDayDetail(row.userId, date);
    },
    async fetchDayDetail(userId, date) {
      try {
        const { data } = await api.get("/api/cham-cong/admin/day", {
          params: { userId, date }
        });
        const detail = data?.data || null;
        this.dayModal.detail = detail;

        const getHHmm = iso =>
          iso
            ? new Date(iso).toLocaleTimeString("vi-VN", {
                hour12: false,
                hour: "2-digit",
                minute: "2-digit"
              })
            : "";

        const sang = detail?.chiTietBuoi?.find(x => x.buoi === "SANG");
        const chieu = detail?.chiTietBuoi?.find(x => x.buoi === "CHIEU");
        this.adjustForm.sangVaoTime = getHHmm(sang?.gioVao);
        this.adjustForm.sangRaTime = getHHmm(sang?.gioRa);
        this.adjustForm.chieuVaoTime = getHHmm(chieu?.gioVao);
        this.adjustForm.chieuRaTime = getHHmm(chieu?.gioRa);

        this.adjustForm.soCongStr =
          detail?.soCong != null ? String(Number(detail.soCong).toFixed(2)) : "";
      } catch (e) {
        console.error(e);
        this.dayModal.detail = null;
      }
    },
    closeDayModal() {
      this.dayModal.open = false;
    },
    composeIso(dateStr, timeStr) {
      return timeStr && timeStr.trim() !== "" ? `${dateStr}T${timeStr}` : null;
    },
    async saveAdjust() {
      if (!this.dayModal.row || !this.dayModal.date) return;
      this.savingAdjust = true;
      try {
        const soCong = this.numOrNull(this.adjustForm.soCongStr);
        const p = {
          userId: this.dayModal.row.userId,
          date: this.dayModal.date,
          soCong,
          sangVao: this.composeIso(this.dayModal.date, this.adjustForm.sangVaoTime),
          sangRa: this.composeIso(this.dayModal.date, this.adjustForm.sangRaTime),
          chieuVao: this.composeIso(this.dayModal.date, this.adjustForm.chieuVaoTime),
          chieuRa: this.composeIso(this.dayModal.date, this.adjustForm.chieuRaTime)
        };

        await api.post("/api/cham-cong/admin/adjust", p);

        await this.fetchDayDetail(this.dayModal.row.userId, this.dayModal.date);
        await this.fetchMatrix();

        this.toastOk("Đã lưu chỉnh sửa");
      } catch (e) {
        console.error(e);
        this.alertUser(e?.response?.data?.message || "Lưu chỉnh sửa lỗi", "error");
      } finally {
        this.savingAdjust = false;
      }
    },
    iconHalf(cell, half) {
      const present = half === "AM" ? cell.amPresent : cell.pmPresent;
      const open = half === "AM" ? cell.amOpen : cell.pmOpen;
      const early = half === "AM" ? cell.amEarly : cell.pmEarly;

      if (open === true) return "fa fa-clock-o text-warning";
      if (present === true && early) return "fa fa-sign-out text-warning";
      if (present === true) return "fa fa-check text-success";
      if (present === false) return "fa fa-times text-danger";

      switch (cell.status) {
        case "FULL":
          return "fa fa-check text-success";
        case "HALF_AM":
          return half === "AM" ? "fa fa-check text-success" : "fa fa-times text-danger";
        case "HALF_PM":
          return half === "PM" ? "fa fa-check text-success" : "fa fa-times text-danger";
        case "OPEN":
          return "fa fa-clock-o text-warning";
        default:
          return "fa fa-times text-danger";
      }
    },
    iconQuarter(cell, key) {
      const map = {
        AM_IN: { present: cell.amPresent, open: cell.amOpen, early: false },
        AM_OUT: { present: cell.amPresent, open: cell.amOpen, early: cell.amEarly },
        PM_IN: { present: cell.pmPresent, open: cell.pmOpen, early: false },
        PM_OUT: { present: cell.pmPresent, open: cell.pmOpen, early: cell.pmEarly }
      };
      const meta = map[key] || {};
      const present = meta.present;
      const open = meta.open;
      const early = meta.early;

      if (open === true && key.endsWith("OUT")) return "fa fa-clock-o text-warning";
      if (present === true && early) return "fa fa-sign-out text-warning";
      if (present === true) {
        return key.endsWith("IN") ? "fa fa-sign-in text-success" : "fa fa-check text-success";
      }
      if (present === false) return "fa fa-times text-danger";
      return "fa fa-minus text-muted";
    },
    exportExcel() {
      if (!this.matrix) return;

      const days = this.matrix.daysInMonth;
      const [y, m] = this.filter.month.split("-").map(Number);

      const COL_STT = 0;
      const COL_NAME = 1;
      const COL_SHIFT = 2;
      const COL_DAY_START = 3;
      const COL_DAY_END = COL_DAY_START + days - 1;
      const COL_TONG_CONG = COL_DAY_END + 1;
      const COL_VAO_TRE = COL_TONG_CONG + 1;
      const COL_VAO_SOM = COL_VAO_TRE + 1;
      const COL_RA_SOM = COL_VAO_SOM + 1;
      const COL_QUEN_CHAM = COL_RA_SOM + 1;
      const COL_NGHI_PHEP = COL_QUEN_CHAM + 1;
      const COL_LENH = COL_NGHI_PHEP + 1;
      const COL_SIGN = COL_LENH + 1;
      const LAST_COL = COL_SIGN;

      const company =
        "CÔNG TY TNHH XUẤT NHẬP KHẨU THÉP & THIẾT BỊ CÔNG NGHIỆP MINH TIẾN – 1997 Huỳnh Văn Lũy, P. Phú Mỹ, TP. Thủ Dầu Một, Bình Dương";
      const title = `BẢNG CHẤM CÔNG THÁNG ${String(m).padStart(2, "0")}/${y}`;
      const deptRow = `BỘ PHẬN ${this.departmentLabel()}`;

      const aoa = [];
      aoa.push([company]);
      aoa.push([title]);

      // Header row 1
      const h1 = [];
      h1[COL_STT] = "STT";
      h1[COL_NAME] = "HỌ VÀ TÊN";
      h1[COL_SHIFT] = "Buổi";
      h1[COL_DAY_START] = "Ngày";
      for (let c = COL_DAY_START + 1; c <= COL_DAY_END; c++) h1[c] = null;
      h1[COL_TONG_CONG] = "Tổng công";
      h1[COL_VAO_TRE] = "Phút đi trễ";
      h1[COL_VAO_SOM] = "Phút vào sớm";
      h1[COL_RA_SOM] = "Phút về sớm";
      h1[COL_QUEN_CHAM] = "Quên chấm";
      h1[COL_NGHI_PHEP] = "Lần nghỉ phép";
      h1[COL_LENH] = "Số lệnh";
      h1[COL_SIGN] = "Ký nhận";
      aoa.push(h1);

      // Header row 2: day numbers
      const h2 = [];
      h2[COL_STT] = null;
      h2[COL_NAME] = null;
      h2[COL_SHIFT] = null;
      for (let d = 1; d <= days; d++) h2[COL_DAY_START + d - 1] = d;
      h2[COL_TONG_CONG] = null;
      h2[COL_VAO_TRE] = null;
      h2[COL_VAO_SOM] = null;
      h2[COL_RA_SOM] = null;
      h2[COL_QUEN_CHAM] = null;
      h2[COL_NGHI_PHEP] = null;
      h2[COL_LENH] = null;
      h2[COL_SIGN] = null;
      aoa.push(h2);

      // Header row 3: weekday
      const h3 = [];
      h3[COL_STT] = null;
      h3[COL_NAME] = null;
      h3[COL_SHIFT] = null;
      const dow = ["CN", "T2", "T3", "T4", "T5", "T6", "T7"];
      for (let d = 1; d <= days; d++) {
        const dt = new Date(y, m - 1, d);
        h3[COL_DAY_START + d - 1] = dow[dt.getDay()];
      }
      h3[COL_TONG_CONG] = null;
      h3[COL_VAO_TRE] = null;
      h3[COL_VAO_SOM] = null;
      h3[COL_RA_SOM] = null;
      h3[COL_QUEN_CHAM] = null;
      h3[COL_NGHI_PHEP] = null;
      h3[COL_LENH] = null;
      h3[COL_SIGN] = null;
      aoa.push(h3);

      // Dept row
      aoa.push([deptRow]);

      // Body rows
      let stt = 1;
      (this.matrix.rows || []).forEach(r => {
        const am = [];
        const pm = [];
        am[COL_STT] = stt;
        pm[COL_STT] = null;
        am[COL_NAME] = r.hoTen;
        pm[COL_NAME] = null;
        am[COL_SHIFT] = "Sáng";
        pm[COL_SHIFT] = "Chiều";

        let sumVaoTre = 0;
        let sumVaoSom = 0;
        let sumRaSom = 0;
        let sumQuen = 0;
        let cntNghiFull = 0;
        let cntNghiAm = 0;
        let cntNghiPm = 0;
        let sumLenh = 0;

        (r.daily || []).forEach(cell => {
          const col = COL_DAY_START + (cell.day - 1);
          const dayV = this.numOrNull(cell.soCong);
          const amV = this.numOrNull(cell.soCongAm);
          const pmV = this.numOrNull(cell.soCongPm);

          if (amV !== null || pmV !== null) {
            am[col] = amV !== null ? +amV.toFixed(2) : 0;
            pm[col] = pmV !== null ? +pmV.toFixed(2) : 0;
          } else if (dayV !== null) {
            const halfAm = Math.min(0.5, Math.max(0, +Number(Math.min(0.5, dayV)).toFixed(2)));
            const halfPm = Math.min(0.5, Math.max(0, +Number(dayV - halfAm).toFixed(2)));
            am[col] = halfAm;
            pm[col] = halfPm;
          } else {
            am[col] = cell.amPresent === true ? 0.5 : 0;
            pm[col] = cell.pmPresent === true ? 0.5 : 0;
          }

          const vt = this.numOrNull(cell.tongVaoTre);
          const vs = this.numOrNull(cell.tongVaoSom);
          const rs = this.numOrNull(cell.tongRaSom);
          const qc = this.numOrNull(cell.tongQuenCham);
          const lenh = this.numOrNull(cell.soLenhHoanThanh);

          if (vt) sumVaoTre += vt;
          if (vs) sumVaoSom += vs;
          if (rs) sumRaSom += rs;
          if (qc) sumQuen += qc;
          if (cell.nghiPhepFull) cntNghiFull += 1;
          if (cell.nghiPhepAm) cntNghiAm += 1;
          if (cell.nghiPhepPm) cntNghiPm += 1;
          if (lenh) sumLenh += lenh;
        });

        const total = r.totalCong ?? this.sumRowCong(r);
        am[COL_TONG_CONG] = Number(total.toFixed(2));
        pm[COL_TONG_CONG] = null;

        am[COL_VAO_TRE] = sumVaoTre;
        am[COL_VAO_SOM] = sumVaoSom;
        am[COL_RA_SOM] = sumRaSom;
        am[COL_QUEN_CHAM] = sumQuen;
        am[COL_NGHI_PHEP] = cntNghiFull + cntNghiAm + cntNghiPm;
        am[COL_LENH] = sumLenh;

        pm[COL_VAO_TRE] = null;
        pm[COL_VAO_SOM] = null;
        pm[COL_RA_SOM] = null;
        pm[COL_QUEN_CHAM] = null;
        pm[COL_NGHI_PHEP] = null;
        pm[COL_LENH] = null;

        am[COL_SIGN] = "";
        pm[COL_SIGN] = null;

        aoa.push(am);
        aoa.push(pm);
        stt++;
      });

      const ws = XLSX.utils.aoa_to_sheet(aoa);

      // Merges
      ws["!merges"] = ws["!merges"] || [];
      ws["!merges"].push({ s: { r: 0, c: 0 }, e: { r: 0, c: LAST_COL } });
      ws["!merges"].push({ s: { r: 1, c: 0 }, e: { r: 1, c: LAST_COL } });
      ws["!merges"].push({ s: { r: 5, c: 0 }, e: { r: 5, c: LAST_COL } });

      const headerCols = [
        COL_STT,
        COL_NAME,
        COL_SHIFT,
        COL_TONG_CONG,
        COL_VAO_TRE,
        COL_VAO_SOM,
        COL_RA_SOM,
        COL_QUEN_CHAM,
        COL_NGHI_PHEP,
        COL_LENH,
        COL_SIGN
      ];
      headerCols.forEach(col => {
        ws["!merges"].push({ s: { r: 2, c: col }, e: { r: 4, c: col } });
      });

      ws["!merges"].push({
        s: { r: 2, c: COL_DAY_START },
        e: { r: 2, c: COL_DAY_END }
      });

      let bodyRow = 6;
      (this.matrix.rows || []).forEach(() => {
        ws["!merges"].push({ s: { r: bodyRow, c: COL_STT }, e: { r: bodyRow + 1, c: COL_STT } });
        ws["!merges"].push({
          s: { r: bodyRow, c: COL_NAME },
          e: { r: bodyRow + 1, c: COL_NAME }
        });
        ws["!merges"].push({
          s: { r: bodyRow, c: COL_TONG_CONG },
          e: { r: bodyRow + 1, c: COL_TONG_CONG }
        });
        ws["!merges"].push({
          s: { r: bodyRow, c: COL_VAO_TRE },
          e: { r: bodyRow + 1, c: COL_VAO_TRE }
        });
        ws["!merges"].push({
          s: { r: bodyRow, c: COL_VAO_SOM },
          e: { r: bodyRow + 1, c: COL_VAO_SOM }
        });
        ws["!merges"].push({
          s: { r: bodyRow, c: COL_RA_SOM },
          e: { r: bodyRow + 1, c: COL_RA_SOM }
        });
        ws["!merges"].push({
          s: { r: bodyRow, c: COL_QUEN_CHAM },
          e: { r: bodyRow + 1, c: COL_QUEN_CHAM }
        });
        ws["!merges"].push({
          s: { r: bodyRow, c: COL_NGHI_PHEP },
          e: { r: bodyRow + 1, c: COL_NGHI_PHEP }
        });
        ws["!merges"].push({
          s: { r: bodyRow, c: COL_LENH },
          e: { r: bodyRow + 1, c: COL_LENH }
        });
        ws["!merges"].push({
          s: { r: bodyRow, c: COL_SIGN },
          e: { r: bodyRow + 1, c: COL_SIGN }
        });
        bodyRow += 2;
      });

      // Col widths
      ws["!cols"] = [];
      ws["!cols"][COL_STT] = { wch: 5 };
      ws["!cols"][COL_NAME] = { wch: 28 };
      ws["!cols"][COL_SHIFT] = { wch: 8 };
      for (let c = COL_DAY_START; c <= COL_DAY_END; c++) ws["!cols"][c] = { wch: 5 };
      ws["!cols"][COL_TONG_CONG] = { wch: 9 };
      ws["!cols"][COL_VAO_TRE] = { wch: 11 };
      ws["!cols"][COL_VAO_SOM] = { wch: 11 };
      ws["!cols"][COL_RA_SOM] = { wch: 11 };
      ws["!cols"][COL_QUEN_CHAM] = { wch: 10 };
      ws["!cols"][COL_NGHI_PHEP] = { wch: 11 };
      ws["!cols"][COL_LENH] = { wch: 9 };
      ws["!cols"][COL_SIGN] = { wch: 12 };

      ws["!pageSetup"] = { orientation: "landscape", fitToWidth: 1, fitToHeight: 0 };

      const borderAll = {
        top: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } }
      };
      const center = { horizontal: "center", vertical: "center" };

      const setS = (r, c, style) => {
        const addr = XLSX.utils.encode_cell({ r, c });
        if (!ws[addr]) ws[addr] = { t: "s", v: "" };
        ws[addr].s = { ...(ws[addr].s || {}), ...style };
      };
      const styleRange = (r1, c1, r2, c2, style) => {
        for (let r = r1; r <= r2; r++) {
          for (let c = c1; c <= c2; c++) {
            setS(r, c, style);
          }
        }
      };

      // Titles
      styleRange(0, 0, 0, LAST_COL, { font: { bold: true, sz: 11 }, alignment: center });
      styleRange(1, 0, 1, LAST_COL, { font: { bold: true, sz: 14 }, alignment: center });
      // Header block
      styleRange(2, 0, 4, LAST_COL, {
        font: { bold: true },
        alignment: center,
        border: borderAll
      });
      // Dept row
      styleRange(5, 0, 5, LAST_COL, {
        font: { italic: true },
        alignment: center,
        border: borderAll
      });

      const startBodyRow = 6;
      const endRow = startBodyRow + (this.matrix.rows?.length || 0) * 2 - 1;
      if (endRow >= startBodyRow) {
        styleRange(startBodyRow, COL_STT, endRow, COL_STT, {
          alignment: center,
          border: borderAll
        });
        styleRange(startBodyRow, COL_SHIFT, endRow, COL_SHIFT, {
          alignment: center,
          border: borderAll
        });
        styleRange(startBodyRow, COL_NAME, endRow, COL_NAME, {
          alignment: { horizontal: "left", vertical: "center" },
          border: borderAll
        });
        styleRange(startBodyRow, COL_DAY_START, endRow, COL_DAY_END, {
          alignment: center,
          border: borderAll,
          numFmt: "0.00"
        });
        styleRange(startBodyRow, COL_TONG_CONG, endRow, COL_TONG_CONG, {
          alignment: center,
          border: borderAll,
          numFmt: "0.00"
        });
        styleRange(startBodyRow, COL_VAO_TRE, endRow, COL_LENH, {
          alignment: center,
          border: borderAll,
          numFmt: "0"
        });
        styleRange(startBodyRow, COL_SIGN, endRow, COL_SIGN, {
          alignment: center,
          border: borderAll
        });
      }

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, `ChamCong_${this.filter.month}`);
      XLSX.writeFile(wb, `BangChamCong_${this.filter.month}.xlsx`);
      this.toastOk("Đã xuất Excel số công + thống kê chi tiết");
    },
    exportExcelUser(row) {
      if (!row || !this.matrix) return;
      const days = this.matrix.daysInMonth || 0;
      const [y, m] = this.filter.month.split("-").map(Number);
      const realRow =
        (this.matrix.rows || []).find(r => r.userId === row.userId) || row;

      const company =
        "CÔNG TY TNHH XUẤT NHẬP KHẨU THÉP & THIẾT BỊ CÔNG NGHIỆP MINH TIẾN – 1997 Huỳnh Văn Lũy, P. Phú Mỹ, TP. Thủ Dầu Một, Bình Dương";
      const title = `BẢNG CHẤM CÔNG CÁ NHÂN THÁNG ${String(m).padStart(2, "0")}/${y}`;

      const stats = this.calcRowStats(realRow);
      const aoa = [];

      aoa.push([company]);
      aoa.push([title]);
      aoa.push([`Họ tên: ${realRow.hoTen}`]);
      aoa.push([`Phòng ban: ${realRow.phongBan || "—"}`]);
      aoa.push([]);

      const header = [
        "Ngày",
        "Thứ",
        "Số công",
        "Phút đi trễ",
        "Phút vào sớm",
        "Phút về sớm",
        "Quên chấm",
        "Nghỉ phép",
        "Số lệnh"
      ];
      aoa.push(header);

      for (let d = 1; d <= days; d++) {
        const dt = new Date(y, m - 1, d);
        const cell = (realRow.daily || []).find(c => c.day === d) || {};
        const cong = this.cellCong(cell);
        const vaoTre = this.numOrNull(cell.tongVaoTre) || 0;
        const vaoSom = this.numOrNull(cell.tongVaoSom) || 0;
        const raSom = this.numOrNull(cell.tongRaSom) || 0;
        const quen = this.numOrNull(cell.tongQuenCham) || 0;

        const leaveFlags = {
          full: !!cell.nghiPhepFull,
          am: !!cell.nghiPhepAm,
          pm: !!cell.nghiPhepPm
        };
        let leaveDisplay = "";
        if (leaveFlags.full) leaveDisplay = "Nghỉ phép (cả ngày)";
        else if (leaveFlags.am && leaveFlags.pm) leaveDisplay = "Nghỉ phép (S+C)";
        else if (leaveFlags.am) leaveDisplay = "Nghỉ phép sáng";
        else if (leaveFlags.pm) leaveDisplay = "Nghỉ phép chiều";

        const lenh = this.numOrNull(cell.soLenhHoanThanh) || 0;

        aoa.push([
          dt.toLocaleDateString("vi-VN", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric"
          }),
          dt.toLocaleDateString("vi-VN", { weekday: "short" }),
          cong,
          vaoTre,
          vaoSom,
          raSom,
          quen,
          leaveDisplay,
          lenh
        ]);
      }

      aoa.push([]);
      const leaveSummary = `${stats.totalNghiPhepFull}N / ${stats.totalNghiPhepAm}S / ${stats.totalNghiPhepPm}C`;
      const totalRow = [];
      totalRow[0] = "TỔNG";
      totalRow[1] = "";
      totalRow[2] = stats.totalCong;
      totalRow[3] = stats.totalMinutesVaoTre;
      totalRow[4] = stats.totalMinutesVaoSom;
      totalRow[5] = stats.totalMinutesRaSom;
      totalRow[6] = stats.totalQuenCham;
      totalRow[7] = leaveSummary;
      totalRow[8] = stats.totalLenhHoanThanh;
      aoa.push(totalRow);

      const ws = XLSX.utils.aoa_to_sheet(aoa);

      const LAST_COL = 8;
      ws["!merges"] = ws["!merges"] || [];
      ws["!merges"].push({ s: { r: 0, c: 0 }, e: { r: 0, c: LAST_COL } });
      ws["!merges"].push({ s: { r: 1, c: 0 }, e: { r: 1, c: LAST_COL } });
      ws["!merges"].push({ s: { r: 2, c: 0 }, e: { r: 2, c: LAST_COL } });
      ws["!merges"].push({ s: { r: 3, c: 0 }, e: { r: 3, c: LAST_COL } });

      ws["!cols"] = [
        { wch: 12 },
        { wch: 8 },
        { wch: 9 },
        { wch: 11 },
        { wch: 11 },
        { wch: 11 },
        { wch: 10 },
        { wch: 20 },
        { wch: 9 }
      ];

      const borderAll = {
        top: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } }
      };
      const center = { horizontal: "center", vertical: "center" };

      const setS = (r, c, style) => {
        const addr = XLSX.utils.encode_cell({ r, c });
        if (!ws[addr]) ws[addr] = { t: "s", v: "" };
        ws[addr].s = { ...(ws[addr].s || {}), ...style };
      };
      const styleRange = (r1, c1, r2, c2, style) => {
        for (let r = r1; r <= r2; r++) {
          for (let c = c1; c <= c2; c++) {
            setS(r, c, style);
          }
        }
      };

      styleRange(0, 0, 3, LAST_COL, { font: { bold: true }, alignment: center });

      const headerRow = 5;
      styleRange(headerRow, 0, headerRow, LAST_COL, {
        font: { bold: true },
        alignment: center,
        border: borderAll
      });

      const firstDataRow = 6;
      const lastDataRow = firstDataRow + days - 1;
      if (lastDataRow >= firstDataRow) {
        styleRange(firstDataRow, 0, lastDataRow, LAST_COL, { border: borderAll });
        styleRange(firstDataRow, 0, lastDataRow, 1, { alignment: center });
        styleRange(firstDataRow, 2, lastDataRow, 6, {
          alignment: center,
          numFmt: "0"
        });
        styleRange(firstDataRow, 8, lastDataRow, 8, {
          alignment: center,
          numFmt: "0"
        });
      }

      const blankRow = lastDataRow + 1;
      const totalRowIdx = blankRow + 1;
      styleRange(totalRowIdx, 0, totalRowIdx, LAST_COL, {
        border: borderAll,
        font: { bold: true }
      });
      styleRange(totalRowIdx, 0, totalRowIdx, 1, { alignment: center });
      styleRange(totalRowIdx, 2, totalRowIdx, 6, {
        alignment: center,
        numFmt: "0"
      });
      styleRange(totalRowIdx, 8, totalRowIdx, 8, {
        alignment: center,
        numFmt: "0"
      });

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, `NV_${realRow.userId}`);
      XLSX.writeFile(
        wb,
        `ChamCong_${realRow.userId}_${this.filter.month}.xlsx`
      );
      this.toastOk("Đã xuất Excel cá nhân");
    },
    openLightbox(imgs, index = 0) {
      this.lightboxImgs = imgs;
      this.lightboxIndex = index;
      this.lightboxVisible = true;
    },
    onHideLightbox() {
      this.lightboxVisible = false;
      this.lightboxImgs = [];
      this.lightboxIndex = 0;
    },
    initials(name) {
      const n = (name || "").trim().split(/\s+/);
      return ((n[0]?.[0] || "") + (n[n.length - 1]?.[0] || "")).toUpperCase();
    },
    formatDateHuman(dt) {
      const d = new Date(dt);
      return d.toLocaleDateString("vi-VN", {
        weekday: "short",
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
      });
    },
    formatDT(dt) {
      const d = new Date(dt);
      return d.toLocaleString("vi-VN", { hour12: false });
    },
    formatTime(dt) {
      const d = new Date(dt);
      return d.toLocaleTimeString("vi-VN", {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit"
      });
    },
    todayStr() {
      const now = new Date();
      return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(now.getDate()).padStart(2, "0")}`;
    },
    fmt(n) {
      return Number(n || 0).toLocaleString("vi-VN");
    },
    prevMonth() {
      const [y, m] = this.filter.month.split("-").map(Number);
      const d = new Date(y, m - 2, 1);
      this.filter.month = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0"
      )}`;
      this.fetchMatrix();
    },
    nextMonth() {
      const [y, m] = this.filter.month.split("-").map(Number);
      const d = new Date(y, m, 1);
      this.filter.month = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0"
      )}`;
      this.fetchMatrix();
    },
    toggleSelectAll() {
      if (!this.matrix) return;
      this.selectedUserIds = this.selectAll ? this.matrix.rows.map(r => r.userId) : [];
    },
    openUserDetail(row) {
      this.userModal = { open: true, row };
    },
    closeUserModal() {
      this.userModal = { open: false, row: null };
    },
    calcRowStats(row) {
      const stats = {
        totalCong: 0,
        totalMinutesVaoTre: 0,
        totalMinutesVaoSom: 0,
        totalMinutesRaSom: 0,
        totalQuenCham: 0,
        totalNghiPhepFull: 0,
        totalNghiPhepAm: 0,
        totalNghiPhepPm: 0,
        totalLenhHoanThanh: 0
      };
      if (!row) return stats;
      (row.daily || []).forEach(c => {
        const cong = this.numOrNull(c.soCong != null ? c.soCong : c.congNgay);
        if (cong !== null) stats.totalCong += cong;

        const vt = this.numOrNull(c.tongVaoTre);
        const vs = this.numOrNull(c.tongVaoSom);
        const rs = this.numOrNull(c.tongRaSom);
        const qc = this.numOrNull(c.tongQuenCham);
        const lenh = this.numOrNull(c.soLenhHoanThanh);

        if (vt) stats.totalMinutesVaoTre += vt;
        if (vs) stats.totalMinutesVaoSom += vs;
        if (rs) stats.totalMinutesRaSom += rs;
        if (qc) stats.totalQuenCham += qc;
        if (c.nghiPhepFull) stats.totalNghiPhepFull += 1;
        if (c.nghiPhepAm) stats.totalNghiPhepAm += 1;
        if (c.nghiPhepPm) stats.totalNghiPhepPm += 1;
        if (lenh) stats.totalLenhHoanThanh += lenh;
      });
      stats.totalCong = +stats.totalCong.toFixed(2);
      return stats;
    },
    toastOk(msg) {
      if (this.$toast?.success) this.$toast.success(msg);
      else alert(msg);
    },
    alertUser(msg, type = "info") {
      if (Swal?.fire) Swal.fire({ icon: type, title: msg });
      else alert(msg);
    }
  }
};
</script>

<style scoped>
.page-header {
  margin-bottom: 12px;
}
.month-nav .btn {
  min-width: 32px;
}

/* Small label helper */
.form-label-sm {
  font-size: 12px;
  margin-bottom: 4px;
  color: #555;
}

/* Stats row */
.stats-row {
  gap: 6px;
}
.mini-stat {
  min-width: 130px;
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
  background: #fafafa;
  margin: 3px 6px 3px 0;
}
.mini-stat-label {
  font-size: 11px;
  color: #777;
}
.mini-stat-value {
  font-size: 15px;
  font-weight: 600;
}

/* Table wrapper */
.attendance-table-wrapper {
  max-height: calc(100vh - 320px);
  overflow: auto;
}

/* Sticky columns + header */
.attendance-table thead th {
  position: sticky;
  top: 0;
  z-index: 3;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
}
.sticky-col-left {
  position: sticky;
  left: 0;
  z-index: 4;
  background: #fff;
}
.name-col {
  min-width: 220px;
}
.total-col {
  min-width: 80px;
}

/* Column cells */
.day-col {
  min-width: 32px;
  width: 32px;
  text-align: center;
  padding: 3px 2px;
  vertical-align: middle;
}
.head-day .dow {
  font-size: 11px;
  color: #666;
  line-height: 1;
}
.head-day .daynum {
  font-weight: 600;
  font-size: 12px;
  line-height: 1.1;
}

/* States */
.sun-head .dow,
.sunday-cell {
  color: #b71c1c !important;
}
.sunday-cell {
  background: #fcfcfc;
}
.holiday-cell {
  background: #fff7f8;
}
.leave-cell {
  background: #fffdf5;
}
.lenh-cell {
  box-shadow: inset 0 -2px 0 #42a5f5;
}
.today-cell {
  outline: 2px solid rgba(25, 118, 210, 0.4);
  outline-offset: -2px;
}

/* Hover */
.day-cell {
  cursor: pointer;
  transition: background-color 0.08s ease-in-out;
}
.day-cell:hover {
  background: #e3f2fd;
}

/* Icon mode */
.table .fa {
  font-size: 12px;
}
/* 2x: 2 icon (S/C) */
.half-day {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}
.half-slot {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 14px;
  line-height: 1;
}

/* 2x2: 4 icon (Sáng/Chiều x In/Out) */
.quad-day {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, 1fr);
  width: 100%;
  height: 100%;
  align-items: center;
  justify-items: center;
}
.quad-slot {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
}

/* Số công */
.cong-box {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 22px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 11px;
  border: 1px solid #eee;
}
.cong-zero {
  color: #999;
  background: #f6f7f8;
}
.cong-mid {
  color: #0d47a1;
  background: #e3f2fd;
  border-color: #d6eaf8;
}
.cong-strong {
  color: #0b4f2f;
  background: #e6f4ea;
  border-color: #d9eadb;
}

/* Total badge */
.badge-soft-primary {
  background: #e3f2fd;
  color: #0d47a1;
  font-weight: 600;
}

/* Avatar */
.avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  overflow: hidden;
  display: inline-block;
  vertical-align: middle;
}
.avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.avatar-fallback {
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #eceff1;
  color: #607d8b;
  font-weight: 700;
  border-radius: 50%;
  font-size: 11px;
}

/* Name truncate + clickable */
.truncate-name {
  max-width: 220px;
}
.truncate-name > div {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.user-name-btn {
  font-size: 13px;
  color: #1976d2;
}
.user-name-btn:hover {
  text-decoration: underline;
}

/* Legend */
.legend-wrap {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 4px;
}
.legend-item {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
}
.legend-dot {
  display: inline-block;
  width: 12px;
  height: 7px;
  border-radius: 8px;
  margin-right: 4px;
  border: 1px solid #ddd;
}
.legend-leave {
  background: #fff3cd;
  border-color: #ffeeba;
}
.legend-holiday {
  background: #fce4ec;
  border-color: #f8bbd0;
}
.legend-lenh {
  background: #e3f2fd;
  border-color: #bbdefb;
}

/* Modal */
.modal {
  background: rgba(0, 0, 0, 0.35);
}
.modal.custom-modal .modal-dialog {
  margin: 0.5rem auto;
}
.modal.custom-modal .modal-content {
  max-height: calc(100vh - 1rem);
  display: flex;
  flex-direction: column;
}
.modal.custom-modal .modal-body {
  overflow-y: auto;
}

/* Modal images */
.img-thumb {
  width: 96px;
  height: 64px;
  object-fit: cover;
  border: 1px solid #eee;
  border-radius: 6px;
}

/* Badge compact */
.badge-compact {
  font-size: 10px;
  padding: 2px 6px;
}
.font-weight-600 {
  font-weight: 600;
}
</style>
